import type { Capability, CapabilityFile, CapabilityManifest, CapabilityRef, McpScope } from "@agentsaas/protocol";

/**
 * P7 能力包的纯逻辑（doc/capabilities.md §1），mock 与界面共用：
 *   - 合并规则：mcpScopes / allowedTools / egress 取并集；permissionRules 按顺序追加；
 *     镜像：模板显式指定了非默认镜像时以模板为准，否则取能力包要求的镜像，多个不同 → 冲突；
 *     skill 目录重名：后引用的覆盖先引用的，记一条告警
 *   - 统计、分类、文件树、语言推断
 */

export const DEFAULT_IMAGE = "agentsaas/agent-base:latest";

export const CATEGORIES = ["art", "code", "test", "planning", "orchestration", "ops", "other"] as const;

/** 能力包需要的"工具"数量：平台 MCP 分组 + 预授权内置工具 + 外部 MCP 服务 */
export function toolCount(m: CapabilityManifest): number {
  return m.tools.mcpScopes.length + m.tools.allowedTools.length + m.tools.externalMcp.length;
}

/** 同一 (租户, id) 只保留最新版本；租户同名能力包覆盖内置 */
export function latestOnly(items: Capability[]): Capability[] {
  const byId = new Map<string, Capability>();
  for (const c of items) {
    const cur = byId.get(c.id);
    const better = !cur || (cur.tenantId === null && c.tenantId !== null) || (cur.tenantId === c.tenantId && c.version > cur.version);
    if (better) byId.set(c.id, c);
  }
  return [...byId.values()];
}

export interface MergeInput {
  ref: CapabilityRef;
  cap: Capability;
}

export interface MergeResult {
  skills: { name: string; capabilityId: string }[];
  /** 被后引用的能力包覆盖掉的 skill 目录 */
  overridden: { name: string; from: string; by: string }[];
  mcpScopes: McpScope[];
  allowedTools: string[];
  externalMcp: { name: string; serverId: string; capabilityId: string }[];
  egress: string[];
  image: string;
  /** 镜像冲突：各能力包要求的镜像（只有在模板未显式指定时才算冲突） */
  imageConflict: { capabilityId: string; image: string }[] | null;
  /** 引用里缺少、但没有默认值的参数 */
  missingParams: { capabilityId: string; key: string }[];
}

const union = <T,>(a: T[], b: readonly T[]) => [...a, ...b.filter((x) => !a.includes(x))];

export function mergeCapabilities(base: { mcpScopes: McpScope[]; allowedTools: string[]; image: string }, inputs: MergeInput[]): MergeResult {
  let mcpScopes = [...base.mcpScopes];
  let allowedTools = [...base.allowedTools];
  let egress: string[] = [];
  const skills: MergeResult["skills"] = [];
  const overridden: MergeResult["overridden"] = [];
  const externalMcp: MergeResult["externalMcp"] = [];
  const images: { capabilityId: string; image: string }[] = [];
  const missingParams: MergeResult["missingParams"] = [];
  for (const { ref, cap } of inputs) {
    const m = cap.manifest;
    mcpScopes = union(mcpScopes, m.tools.mcpScopes);
    allowedTools = union(allowedTools, m.tools.allowedTools);
    egress = union(egress, m.runtime.egress);
    for (const s of m.skills) {
      const i = skills.findIndex((x) => x.name === s.name);
      if (i >= 0) {
        overridden.push({ name: s.name, from: skills[i]!.capabilityId, by: cap.id });
        skills.splice(i, 1);
      }
      skills.push({ name: s.name, capabilityId: cap.id });
    }
    for (const x of m.tools.externalMcp) if (!externalMcp.some((y) => y.name === x.name)) externalMcp.push({ name: x.name, serverId: x.serverId, capabilityId: cap.id });
    if (m.runtime.image && !images.some((x) => x.image === m.runtime.image)) images.push({ capabilityId: cap.id, image: m.runtime.image });
    for (const p of m.params) if (p.default === undefined && ref.params[p.key] === undefined) missingParams.push({ capabilityId: cap.id, key: p.key });
  }
  const explicit = base.image && base.image !== DEFAULT_IMAGE;
  let image = base.image || DEFAULT_IMAGE;
  let imageConflict: MergeResult["imageConflict"] = null;
  if (!explicit) {
    if (images.length === 1) image = images[0]!.image;
    else if (images.length > 1) imageConflict = images;
  }
  return { skills, overridden, mcpScopes, allowedTools, externalMcp, egress, image, imageConflict, missingParams };
}

/** 实例级增减：模板的引用 + add（同 id 以 add 为准）− remove */
export function applyOverrides(templateRefs: CapabilityRef[], overrides: { add: CapabilityRef[]; remove: string[] } | undefined): { ref: CapabilityRef; source: "template" | "agent" }[] {
  const add = overrides?.add ?? [];
  const remove = new Set(overrides?.remove ?? []);
  const out: { ref: CapabilityRef; source: "template" | "agent" }[] = [];
  for (const r of templateRefs) if (!remove.has(r.id) && !add.some((a) => a.id === r.id)) out.push({ ref: r, source: "template" });
  for (const r of add) if (!remove.has(r.id)) out.push({ ref: r, source: "agent" });
  return out;
}

// ───────────── 文件树 ─────────────

export interface TreeNode {
  name: string;
  /** 相对 skill 根的完整路径 */
  path: string;
  type: "dir" | "file";
  children: TreeNode[];
}

export function buildTree(files: Pick<CapabilityFile, "path">[]): TreeNode[] {
  const root: TreeNode = { name: "", path: "", type: "dir", children: [] };
  for (const f of files) {
    const parts = f.path.split("/");
    let cur = root;
    parts.forEach((part, i) => {
      const path = parts.slice(0, i + 1).join("/");
      const leaf = i === parts.length - 1;
      let next = cur.children.find((c) => c.name === part && c.type === (leaf ? "file" : "dir"));
      if (!next) {
        next = { name: part, path, type: leaf ? "file" : "dir", children: [] };
        cur.children.push(next);
      }
      cur = next;
    });
  }
  const sort = (n: TreeNode) => {
    // SKILL.md 永远排第一，然后目录在前、按名字
    n.children.sort((a, b) => (a.path === "SKILL.md" ? -1 : b.path === "SKILL.md" ? 1 : a.type === b.type ? a.name.localeCompare(b.name) : a.type === "dir" ? -1 : 1));
    n.children.forEach(sort);
  };
  sort(root);
  return root.children;
}

/** 文件树展开后的可见顺序（键盘导航用） */
export function flattenTree(nodes: TreeNode[], open: Set<string>, depth = 0): { node: TreeNode; depth: number }[] {
  return nodes.flatMap((n) => [{ node: n, depth }, ...(n.type === "dir" && open.has(n.path) ? flattenTree(n.children, open, depth + 1) : [])]);
}

/** 文件是否可作为文本展示 */
export function isText(f: Pick<CapabilityFile, "encoding">) {
  return f.encoding !== "base64";
}

export function fileSize(f: Pick<CapabilityFile, "content" | "encoding">): number {
  if (f.encoding === "base64") return Math.floor((f.content.replace(/=+$/, "").length * 3) / 4);
  return new TextEncoder().encode(f.content).length;
}

export function manifestSize(m: CapabilityManifest): number {
  return m.skills.reduce((n, s) => n + s.files.reduce((k, f) => k + fileSize(f), 0), 0);
}

/** 能力包总大小上限（doc/capabilities.md / cap-core：超过 5MB 拒绝） */
export const MAX_TOTAL_BYTES = 5 * 1024 * 1024;

export const SKILL_NAME_RE = /^[a-z0-9][a-z0-9-]{0,63}$/;

/** 把任意名字规整成合法的 skill 目录名 */
export function toSkillName(raw: string): string {
  const s = raw
    .toLowerCase()
    .replace(/\.zip$/, "")
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64);
  return s || "skill";
}

export function validPath(p: string): boolean {
  return !!p && p.length <= 256 && !p.startsWith("/") && !p.split("/").includes("..") && !p.split("/").includes("") && !p.includes("\\");
}

/** 能力包某个版本的稳定键（编辑器 model 路径等） */
export function capKey(c: { tenantId: string | null; id: string; version: number }): string {
  return `${c.tenantId ?? "builtin"}/${c.id}@${c.version}`;
}
